import React, { useEffect, useState } from "react";
import { Lock, FileText, Loader2, ShieldCheck, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { fetchCurrentNda, postAcceptNda } from "../../lib/ndaApi";
import NdaDocumentDialog from "./NdaDocumentDialog";

/**
 * Full-screen blocking overlay shown to authenticated users who have
 * not yet accepted the currently required NDA version.
 *
 * Rendered by <NdaGate/> — never rendered for anonymous visitors.
 * Public pages remain fully accessible without authentication.
 */
export default function NdaGate() {
  const { isAuthenticated, ndaState, refreshMe, logout } = useAuth();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [docOpen, setDocOpen] = useState(false);
  const [showUpdatedBanner, setShowUpdatedBanner] = useState(false);

  const needs = !!(isAuthenticated && ndaState && ndaState.needs_acceptance);

  useEffect(() => {
    if (!needs) return;
    let cancelled = false;
    fetchCurrentNda()
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch(() => {
        if (!cancelled)
          setMetaError("Impossibile caricare l'accordo. Riprova più tardi.");
      });
    return () => {
      cancelled = true;
    };
  }, [needs]);

  // Detect "updated version" case: user has accepted a previous version.
  useEffect(() => {
    if (!needs) return setShowUpdatedBanner(false);
    // For future versions — v1.0 is the initial release so this stays false
    // for the first rollout. When a v1.1/v2.0 lands, the backend status API
    // will surface a previous acceptance via /api/nda/acceptances; we can
    // extend this hook to detect a mismatch. For now: static false.
    setShowUpdatedBanner(false);
  }, [needs]);

  // Prevent body scroll while gate is active.
  useEffect(() => {
    if (!needs) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [needs]);

  if (!needs) return null;

  const handleAccept = async () => {
    if (!checked || busy || !meta?.version) return;
    setError("");
    setBusy(true);
    try {
      await postAcceptNda(meta.version);
      await refreshMe();
      // On success, gate unmounts because needs_acceptance becomes false.
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        (typeof detail === "string" && detail) ||
          e?.message ||
          "Registrazione dell'accettazione non riuscita. Riprova."
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    // Not accepting = we log the user out to prevent reserved-area access.
    try {
      await logout();
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nda-gate-title"
        aria-describedby="nda-gate-desc"
        data-testid="nda-gate"
        className="fixed inset-0 z-[2147482500] flex items-center justify-center px-3 sm:px-6 py-6"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 1rem)",
          paddingBottom: "max(env(safe-area-inset-bottom), 1rem)",
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/95 backdrop-blur-2xl"
          aria-hidden="true"
        />

        {/* Card */}
        <div className="relative w-full max-w-xl max-h-full overflow-y-auto rounded-2xl border border-white/10 bg-[rgba(10,10,12,0.98)] shadow-[0_40px_120px_rgba(0,0,0,0.7)] p-5 sm:p-8">
          {/* Icon + eyebrow */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center">
              <Lock className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/85">
              Accesso Riservato
            </div>
          </div>

          {showUpdatedBanner && (
            <div
              data-testid="nda-gate-updated-banner"
              className="mb-5 rounded-xl border border-amber-400/25 bg-amber-500/[0.06] px-4 py-3 text-[12px] text-amber-200/90 leading-relaxed"
            >
              L&apos;Accordo di Riservatezza TIME Inside è stato aggiornato.
              Per continuare ad accedere alle funzionalità riservate è
              necessario prendere visione e accettare la nuova versione.
            </div>
          )}

          {/* Title */}
          <h2
            id="nda-gate-title"
            data-testid="nda-gate-title"
            className="text-2xl sm:text-3xl font-light text-white leading-tight mb-4"
          >
            Stai per accedere in anteprima a TIME Inside.
          </h2>

          {/* Intro */}
          <p
            id="nda-gate-desc"
            data-testid="nda-gate-desc"
            className="text-sm sm:text-[15px] text-white/70 font-light leading-relaxed mb-6"
          >
            {meta?.summary ||
              "Alcune funzionalità, idee e contenuti che vedrai potrebbero non essere ancora pubblici. Ti chiediamo di non divulgarli, copiarli o condividerli con altre persone senza autorizzazione."}
          </p>

          {/* NDA banner */}
          <div className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-4 mb-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-1.5">
                  Documento
                </div>
                <div className="text-[14px] text-white/85 font-light">
                  Accordo di Riservatezza (NDA)
                </div>
                {meta?.version && (
                  <div className="mt-0.5 text-[11px] tracking-[0.22em] uppercase text-white/40">
                    Versione {meta.version}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setDocOpen(true)}
                data-testid="nda-gate-read-full"
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/15 text-[10px] tracking-[0.24em] uppercase text-white/75 hover:text-white hover:border-white/30 hover:bg-white/[0.04] transition-all flex-shrink-0"
              >
                <FileText className="w-3.5 h-3.5" />
                Leggi
              </button>
            </div>
          </div>

          {metaError && (
            <div
              data-testid="nda-gate-meta-error"
              className="mb-4 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
            >
              {metaError}
            </div>
          )}

          {/* Checkbox */}
          <label
            className="flex items-start gap-3 mb-6 cursor-pointer group select-none"
            htmlFor="nda-gate-check"
          >
            <span className="relative flex items-center justify-center flex-shrink-0 mt-0.5">
              <input
                id="nda-gate-check"
                data-testid="nda-gate-checkbox"
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="peer sr-only"
                aria-describedby="nda-gate-check-label"
              />
              <span
                aria-hidden="true"
                className={[
                  "w-5 h-5 rounded-md border transition-all",
                  checked
                    ? "bg-emerald-400 border-emerald-400"
                    : "bg-transparent border-white/25 group-hover:border-white/50",
                ].join(" ")}
              />
              {checked && (
                <ShieldCheck
                  className="absolute w-3.5 h-3.5 text-black pointer-events-none"
                  aria-hidden="true"
                />
              )}
            </span>
            <span
              id="nda-gate-check-label"
              className="text-[13px] sm:text-sm text-white/80 font-light leading-relaxed"
            >
              {meta?.confirmation_label ||
                "Dichiaro di aver letto e compreso l'Accordo di Riservatezza TIME Inside e di accettarne integralmente le condizioni."}
            </span>
          </label>

          {error && (
            <div
              data-testid="nda-gate-error"
              className="mb-4 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
            >
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleDecline}
              data-testid="nda-gate-decline"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-white/10 text-[11px] tracking-[0.24em] uppercase text-white/50 hover:text-white/80 hover:border-white/25 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Non ora, esci
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!checked || busy || !meta}
              data-testid="nda-gate-accept"
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-400/95 text-black text-[12px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Accetta e continua"
              )}
            </button>
          </div>

          <p className="mt-5 text-center text-[10px] tracking-[0.24em] uppercase text-white/25">
            Accettazione elettronica associata al tuo account
          </p>
        </div>
      </div>

      <NdaDocumentDialog
        open={docOpen}
        version={meta?.version || ndaState?.current_version || "1.0"}
        onClose={() => setDocOpen(false)}
      />
    </>
  );
}
